/**
 * RestoID — social / enterprise-SSO landing (the native-app hop).
 * ---------------------------------------------------------------------------
 * Only reached by a native app signing in OUTSIDE its webview: the page stores the
 * app's callback link and then hands the browser to the identity provider. The
 * logic is the PRISTINE upstream one — `useSocialLandingHandler` is imported from
 * `src/pages/SocialLanding/`, not reimplemented, so the redirect-scheme check
 * (`isValidWebRedirectUri`), the "drop an invalid native link but still redirect"
 * branch (`isValidNativeCallbackLink` -> `storeCallbackLink`) and the
 * `error.invalid_connector_request` toast all keep working untouched. Replaces
 * Logto's `StaticPageLayout` + `SocialLanding` container. Swapped in via
 * `src/App.tsx`.
 *
 * Deliberate differences from upstream, and why:
 *  - The connector logo and name come from the connector METADATA
 *    (`useConnectors().findConnectorById`), exactly as upstream's container does,
 *    so no vendor color is restated here — Yandex's red disc is just the logo
 *    Logto serves. Only the light/dark VARIANT is picked differently: see
 *    `getConnectorLogo` in `custom/components/connector-meta.ts`.
 *  - Upstream renders a bare spinner under the logo. We say where the user is
 *    going, with the existing `description.redirect_to` phrase key.
 *  - `loading` can only ever go false on the handler's failure branch, and upstream
 *    left that state as a logo plus a toast that fades — a dead end. We hand it to
 *    our own ErrorPage, which gives it a way out. No new copy key, no logic change;
 *    the upstream toast still fires.
 *  - `FirstScreenLayout` replaces `StaticPageLayout`, which adds the `PageMeta`
 *    this page never had (`action.sign_in_with`, interpolated with the connector).
 */
import { experience } from '@logto/schemas';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import useConnectors from '@/hooks/use-connectors';
import useSocialLandingHandler from '@/pages/SocialLanding/use-social-landing-handler';

import { getConnectorLogo, getConnectorName } from '../components/connector-meta';
import { C, FONT, Spin, Stack } from '../components/restoid';
import useRidTheme from '../components/use-rid-theme';

import ErrorPage from './ErrorPage';

type RouteParameters = {
  connectorId: string;
};

const SocialLanding = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { connectorId } = useParams<RouteParameters>();
  const { loading, socialLandingHandler } = useSocialLandingHandler();
  const { findConnectorById } = useConnectors();
  const isDark = useRidTheme() === 'dark';

  // SocialSignIn callback handler — upstream effect, unchanged.
  useEffect(() => {
    if (!connectorId) {
      return;
    }
    socialLandingHandler(connectorId);
  }, [connectorId, socialLandingHandler]);

  if (!connectorId) {
    return null;
  }

  // `loading` only ever goes false on the handler's failure branch. Upstream left
  // that state as a logo and a toast that fades, i.e. a dead end. Render the RestoID
  // error screen instead — same phrase key, but with the "back to sign-in" action
  // every other terminal state in the app offers. Restating the toast's sentence
  // inline under the logo was the obvious alternative and is worse: the user reads
  // the identical line twice and still cannot leave.
  if (!loading) {
    return (
      <ErrorPage
        title="error.invalid_connector_request"
        primaryAction={{
          title: 'description.back_to_sign_in',
          to: `/${experience.routes.signIn}`,
          replace: true,
        }}
      />
    );
  }

  const result = findConnectorById(connectorId);
  // Upstream falls back to the bare connector id when the connector is not in the
  // sign-in experience settings; keep that rather than render an empty screen.
  const name = result ? getConnectorName(result, language) : connectorId;
  const logo = result ? getConnectorLogo(result, isDark) : undefined;

  return (
    <FirstScreenLayout
      pageMeta={{ titleKey: 'action.sign_in_with', titleKeyInterpolation: { name } }}
    >
      <Stack gap={18}>
        <div style={{ display: 'grid', placeItems: 'center', padding: '14px 0' }}>
          {/* One plate for both branches: a monochrome connector mark has no contrast
              of its own, so the tile — not the page — is always what it sits on. */}
          <span
            style={{
              display: 'grid',
              placeItems: 'center',
              width: 96,
              height: 96,
              padding: 8,
              borderRadius: 24,
              background: C.tile,
              border: `1px solid ${C.tileLine}`,
              color: C.brand,
              font: `600 15px/1.3 ${FONT}`,
              textAlign: 'center',
              overflowWrap: 'anywhere',
            }}
          >
            {logo ? (
              // Decorative: the provider's name is spelled out in the line below.
              <img
                src={logo}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              name
            )}
          </span>
        </div>
        <p
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            margin: 0,
            color: C.sub,
            font: `400 14px/1.55 ${FONT}`,
            textAlign: 'center',
          }}
        >
          <Spin size={18} />
          <span>{t('description.redirect_to', { name })}</span>
        </p>
      </Stack>
    </FirstScreenLayout>
  );
};

export default SocialLanding;
